/*
  ION Native Pilot
  ----------------
  Progressive enhancement only. Everything these custom elements upgrade is
  already usable without JavaScript:

  - <ion-native-gallery> starts life as a stack of linked media panels; JS
    turns the thumbnail links into a real ARIA tab pattern.
  - <ion-native-accordion> wraps native <details>/<summary> rows, which are
    keyboard and screen-reader accessible on their own; JS only adds the
    optional "one row open at a time" behaviour.

  Custom element names and internal selectors are namespaced `ion-native`. The
  gallery id intentionally follows Shrine's existing MediaGallery convention.
*/

(function () {
  'use strict';

  if (typeof window === 'undefined' || !('customElements' in window)) return;

  /* ---------------------------------------------------------------- gallery */

  if (!customElements.get('ion-native-gallery')) {
    customElements.define(
      'ion-native-gallery',
      class IonNativeGallery extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('.ion-native-gallery__thumb'));
          this.panels = Array.from(this.querySelectorAll('.ion-native-gallery__panel'));

          if (this.tabs.length < 2 || this.panels.length !== this.tabs.length) return;

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onVariantChange = this.onVariantChange.bind(this);

          this.classList.add('ion-native-gallery--enhanced');
          const tablist = this.querySelector('.ion-native-gallery__thumbs');
          tablist.setAttribute('role', 'tablist');
          tablist.setAttribute('aria-label', tablist.dataset.label);

          this.tabs.forEach((tab, index) => {
            tab.setAttribute('role', 'tab');
            tab.setAttribute('aria-controls', this.panels[index].id);
            this.panels[index].setAttribute('role', 'tabpanel');
            this.panels[index].setAttribute('aria-labelledby', tab.id);
            tab.addEventListener('click', this.onClick);
            tab.addEventListener('keydown', this.onKeydown);
          });

          this.select(0, false);

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantChangeUnsubscriber = subscribe(
              PUB_SUB_EVENTS.variantChange,
              this.onVariantChange
            );
          }
        }

        disconnectedCallback() {
          this.tabs.forEach((tab) => {
            tab.removeEventListener('click', this.onClick);
            tab.removeEventListener('keydown', this.onKeydown);
          });
          if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        }

        onClick(event) {
          event.preventDefault();
          this.select(this.tabs.indexOf(event.currentTarget), false);
        }

        onVariantChange(event) {
          const data = event?.data;
          if (!data || String(data.sectionId) !== this.dataset.sectionId) return;
          const featuredMedia = data.variant?.featured_media;
          if (featuredMedia?.id) this.setActiveMedia(`${this.dataset.sectionId}-${featuredMedia.id}`, true);
        }

        onKeydown(event) {
          const current = this.tabs.indexOf(event.currentTarget);
          const last = this.tabs.length - 1;
          let next = null;

          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              next = current === last ? 0 : current + 1;
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              next = current === 0 ? last : current - 1;
              break;
            case 'Home':
              next = 0;
              break;
            case 'End':
              next = last;
              break;
            default:
              return;
          }

          event.preventDefault();
          this.select(next, true);
        }

        select(index, moveFocus) {
          if (index < 0 || index >= this.tabs.length) return;
          this.tabs.forEach((tab, i) => {
            const active = i === index;
            tab.setAttribute('aria-selected', active ? 'true' : 'false');
            // Roving tabindex: only the selected tab is in the tab order.
            tab.setAttribute('tabindex', active ? '0' : '-1');
          });

          this.panels.forEach((panel, i) => {
            const active = i === index;
            panel.toggleAttribute('hidden', !active);
            if (!active) {
              const media = panel.querySelector('video');
              if (media && typeof media.pause === 'function') media.pause();
            }
          });

          if (moveFocus && this.tabs[index]) this.tabs[index].focus();
        }

        // Shrine's VariantSelects calls this method on every matching
        // MediaGallery-* element. Keep its media-id/prepend signature so the
        // pilot works both through that convention and the pub/sub fallback.
        setActiveMedia(mediaId, prepend) {
          const index = this.panels.findIndex((panel) => panel.dataset.mediaId === String(mediaId));
          if (index < 0) return;

          this.select(index, false);
          if (prepend) {
            this.panels[index].parentElement.prepend(this.panels[index]);
            this.tabs[index].parentElement.prepend(this.tabs[index]);
            this.panels = Array.from(this.querySelectorAll('.ion-native-gallery__panel'));
            this.tabs = Array.from(this.querySelectorAll('.ion-native-gallery__thumb'));
          }
        }
      }
    );
  }

  /* -------------------------------------------------------------- accordion */

  if (!customElements.get('ion-native-accordion')) {
    customElements.define(
      'ion-native-accordion',
      class IonNativeAccordion extends HTMLElement {
        connectedCallback() {
          if (this.dataset.singleOpen !== 'true') return;

          this.items = Array.from(this.querySelectorAll('.ion-native-accordion__item'));
          this.onToggle = this.onToggle.bind(this);
          this.items.forEach((item) => item.addEventListener('toggle', this.onToggle));
        }

        disconnectedCallback() {
          if (!this.items) return;
          this.items.forEach((item) => item.removeEventListener('toggle', this.onToggle));
        }

        onToggle(event) {
          const opened = event.currentTarget;
          if (!opened.open) return;
          this.items.forEach((item) => {
            if (item !== opened) item.open = false;
          });
        }
      }
    );
  }

  if (!customElements.get('ion-native-carousel')) {
    customElements.define('ion-native-carousel', class IonNativeCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.ion-native-carousel__viewport');
        this.slides = Array.from(this.querySelectorAll('[data-ion-native-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-ion-native-dot]'));
        if (!this.viewport || this.slides.length < 2) return;
        this.classList.add('ion-native-carousel--enhanced');
        this.index = 0;
        this.querySelector('[data-ion-native-prev]')?.addEventListener('click', () => this.show(this.index - 1));
        this.querySelector('[data-ion-native-next]')?.addEventListener('click', () => this.show(this.index + 1));
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index)));
        this.viewport.addEventListener('scroll', () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => this.setCurrent(Math.round(this.viewport.scrollLeft / this.viewport.clientWidth)), 80);
        }, { passive: true });
        this.setCurrent(0);
      }
      show(index) {
        const next = (index + this.slides.length) % this.slides.length;
        this.viewport.scrollTo({ left: next * this.viewport.clientWidth, behavior: 'smooth' });
        this.setCurrent(next);
      }
      setCurrent(index) {
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === this.index ? 'true' : 'false'));
      }
    });
  }

  /* ------------------------------------------ scoped ambient sales videos */

  const ambientVideos = new Set();
  const replay = (video) => {
    if (!video.isConnected || document.hidden || !video.dataset.ionNativeVisible) return;
    video.muted = true;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  };
  const observeAmbientVideos = (root) => {
    root.querySelectorAll?.('.ion-native video[data-ion-native-autoplay]').forEach((video) => {
      if (ambientVideos.has(video)) return;
      ambientVideos.add(video);
      video.controls = false;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.disablePictureInPicture = true;
      video.addEventListener('pause', () => replay(video));
      ambientObserver.observe(video);
    });
  };
  const ambientObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.dataset.ionNativeVisible = 'true';
      else delete entry.target.dataset.ionNativeVisible;
      if (entry.isIntersecting) replay(entry.target);
    });
  }, { threshold: 0.05 });
  observeAmbientVideos(document);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) ambientVideos.forEach(replay);
  });
  document.addEventListener('shopify:section:load', (event) => observeAmbientVideos(event.target));
})();
